import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { CalendarX, Trash2, Plus } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useToast } from '../hooks/use-toast';
import { getAvailability, setTrailerAvailability, createBlock, deleteBlock } from '../lib/api';

const fmt = (iso) => format(new Date(iso + 'T00:00:00'), 'MMM d, yyyy');

export default function AdminAvailability({ token }) {
  const { toast } = useToast();
  const [trailers, setTrailers] = useState([]);

  const load = () => getAvailability(token).then(setTrailers).catch(() => toast({ title: 'Failed to load availability' }));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggle = async (id, available) => {
    try {
      await setTrailerAvailability(token, id, available);
      setTrailers((prev) => prev.map((t) => (t.id === id ? { ...t, available } : t)));
      toast({ title: available ? 'Trailer is now bookable' : 'Trailer marked unavailable' });
    } catch {
      toast({ title: 'Update failed' });
    }
  };

  const removeBlock = async (id) => {
    try {
      await deleteBlock(token, id);
      load();
    } catch {
      toast({ title: 'Could not remove block' });
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-availability">
      {trailers.map((t) => (
        <div key={t.id} data-testid={`availability-card-${t.id}`} className="bg-[#141a17] border border-white/10 rounded-xl p-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-semibold">{t.name}</div>
              <div className={`text-xs mt-0.5 ${t.available ? 'text-emerald-400' : 'text-rose-400'}`} data-testid={`availability-status-${t.id}`}>
                {t.available ? 'Available for online booking' : 'Unavailable — customers cannot book'}
              </div>
            </div>
            <Switch checked={t.available} onCheckedChange={(v) => toggle(t.id, v)} data-testid={`availability-toggle-${t.id}`} />
          </div>

          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mb-2">Blocked dates</div>
            {t.blocks.length === 0 ? (
              <div className="text-sm text-white/40">No blocked dates.</div>
            ) : (
              <ul className="space-y-2">
                {t.blocks.map((b) => (
                  <li key={b.id} data-testid={`block-row-${b.id}`} className="flex items-center justify-between text-sm bg-white/5 rounded-md px-3 py-2">
                    <span className="flex items-center gap-2 text-white/80">
                      <CalendarX className="w-4 h-4 text-rose-400" />
                      {b.start === b.end ? fmt(b.start) : `${fmt(b.start)} → ${fmt(b.end)}`}
                      {b.reason && <span className="text-white/40">· {b.reason}</span>}
                    </span>
                    <button onClick={() => removeBlock(b.id)} data-testid={`block-delete-${b.id}`} className="text-white/50 hover:text-rose-400" title="Remove block">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <AddBlockForm token={token} trailerId={t.id} onAdded={load} />
          </div>
        </div>
      ))}
    </div>
  );
}

function AddBlockForm({ token, trailerId, onAdded }) {
  const { toast } = useToast();
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [reason, setReason] = useState('');

  const submit = async () => {
    if (!range?.from) return toast({ title: 'Pick dates to block' });
    try {
      await createBlock(token, {
        trailer: trailerId,
        start: format(range.from, 'yyyy-MM-dd'),
        end: format(range.to || range.from, 'yyyy-MM-dd'),
        reason,
      });
      setRange({ from: undefined, to: undefined });
      setReason('');
      toast({ title: 'Dates blocked' });
      onAdded();
    } catch (e) {
      toast({ title: 'Could not block dates', description: String(e?.response?.data?.detail || '') });
    }
  };

  const label = range?.from
    ? range.to && range.to.getTime() !== range.from.getTime()
      ? `${format(range.from, 'MMM d')} → ${format(range.to, 'MMM d, yyyy')}`
      : format(range.from, 'MMM d, yyyy')
    : 'Select dates';

  return (
    <div className="mt-3 flex flex-col sm:flex-row gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" data-testid={`block-dates-picker-${trailerId}`} className="h-9 sm:w-56 justify-start bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white font-normal">
            <CalendarX className="w-4 h-4 mr-2 text-amber-500" /> {label}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar mode="range" disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }} selected={range} onSelect={(r) => setRange(r || { from: undefined, to: undefined })} />
        </PopoverContent>
      </Popover>
      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (optional, e.g. maintenance)"
        data-testid={`block-reason-input-${trailerId}`}
        className="h-9 flex-1 bg-white/5 border-white/15 text-white placeholder:text-white/40"
      />
      <Button onClick={submit} data-testid={`block-add-btn-${trailerId}`} className="h-9 bg-amber-500 hover:bg-amber-400 text-[#132119] font-semibold">
        <Plus className="w-4 h-4 mr-1" /> Block
      </Button>
    </div>
  );
}
